import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Link,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  VStack,
  Collapse,
} from "@chakra-ui/react";
import { Logo } from "../../../Logo";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { CloseIcon, HamburgerIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { SecureMembershipButton, LoginWithSmsModal } from "../../../pages/authentication";
import { useState } from "react";

interface NavItem {
  label: string;
  href?: string;
  children?: NavItem[];
  isButton?: boolean;
}

const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  {
    label: "Services",
    children: [
      { label: "Off Grid Solar System", href: "/services/off-grid-solar" },
      { label: "Residential Solar", href: "/services/residential-solar" },
      { label: "Commercial Solar", href: "/services/commercial-solar" },
      { label: "Battery Installation", href: "/services/battery-installation" },
      { label: "Solar Maintenance", href: "/services/solar-maintenance" },
      { label: "Solar Monitoring", href: "/services/solar-monitoring" },
      { label: "Solar Repairs", href: "/services/solar-repairs" },
      { label: "(VPP) Batteries and Off Grid Systems", href: "/services/vpp-batteries" },
    ],
  },
  { label: "Capability", href: "/capability" },

  { label: "Call Now", href: "tel:+64800767024" },
  { label: "Contact", href: "/contact", isButton: true },
];

export const NavbarWithCallToAction = () => {
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const { isOpen: isMobileOpen, onToggle } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);

  const handleLoginSuccess = () => {
    onLoginClose();
    refreshAuth();
  };

  const handleNavigation = (href: string) => {
    if (href.startsWith("tel:")) {
      window.location.href = href;
    } else if (href.startsWith("http")) {
      window.open(href, "_blank");
    } else {
      navigate(href);
    }
    isMobileOpen && onToggle();
    setOpenMobileDropdown(null);
  };

  const toggleMobileDropdown = (label: string) => {
    setOpenMobileDropdown(openMobileDropdown === label ? null : label);
  };

  return (
    <Box
      position="sticky"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
    >
      <Container maxW="7xl" px={{ base: 4, md: 6 }}>
        <Flex
          minH="120px"
          align="center"
          justify="space-between"
        >
          {/* Logo */}
          <Box cursor="pointer" onClick={() => navigate("/")}>
            <Logo />
          </Box>

          {/* Desktop Navigation */}
          {isDesktop ? (
            <HStack spacing={0} align="center">
              {navItems.map((item) => (
                <Box
                  key={item.label}
                  position="relative"
                  onMouseEnter={() => item.children && setHoveredItem(item.label)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {item.isButton ? (
                    <Button
                      ml={3}
                      bg="#121c47"
                      color="white"
                      px={6}
                      py={2}
                      fontSize="15px"
                      fontWeight="600"
                      borderRadius="4px"
                      onClick={() => item.href && handleNavigation(item.href)}
                      _hover={{
                        bg: "#0a1230",
                        transform: "translateY(-1px)",
                      }}
                      transition="all 0.2s"
                    >
                      {item.label}
                    </Button>
                  ) : item.href?.startsWith("tel:") ? (
                    <Link
                      href={item.href}
                      px={4}
                      py={2}
                      fontSize="15px"
                      fontWeight="500"
                      color="#333"
                      display="flex"
                      alignItems="center"
                      _hover={{
                        color: "#00a651",
                        textDecoration: "none",
                      }}
                      transition="color 0.2s"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Button
                      variant="ghost"
                      px={4}
                      py={2}
                      fontSize="15px"
                      fontWeight="500"
                      color="#333"
                      borderRadius={0}
                      rightIcon={item.children ? <ChevronDownIcon /> : undefined}
                      onClick={() => !item.children && item.href && handleNavigation(item.href)}
                      _hover={{
                        bg: "transparent",
                        color: "#00a651",
                      }}
                      transition="color 0.2s"
                    >
                      {item.label}
                    </Button>
                  )}

                  {/* Desktop Dropdown */}
                  {item.children && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      mt={0}
                      bg="white"
                      boxShadow="lg"
                      borderRadius="4px"
                      border="1px solid"
                      borderColor="gray.200"
                      minW="250px"
                      opacity={hoveredItem === item.label ? 1 : 0}
                      visibility={hoveredItem === item.label ? "visible" : "hidden"}
                      transform={hoveredItem === item.label ? "translateY(0)" : "translateY(-10px)"}
                      transition="all 0.2s"
                      zIndex={10}
                    >
                      <VStack align="stretch" spacing={0} py={2}>
                        {item.children.map((child) => (
                          <Box
                            key={child.label}
                            px={4}
                            py={3}
                            cursor="pointer"
                            onClick={() => child.href && handleNavigation(child.href)}
                            _hover={{
                              bg: "gray.50",
                              color: "#00a651",
                            }}
                            transition="all 0.2s"
                            fontSize="14px"
                            color="#333"
                          >
                            {child.label}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Box>
              ))}
            </HStack>
          ) : (
            /* Mobile Menu Toggle */
            <IconButton
              onClick={onToggle}
              icon={isMobileOpen ? <CloseIcon /> : <HamburgerIcon />}
              variant="ghost"
              aria-label="Toggle Navigation"
              color="#333"
              _hover={{
                bg: "gray.100",
              }}
            />
          )}
        </Flex>

        {/* Mobile Navigation */}
        <Collapse in={isMobileOpen} animateOpacity>
          <Box pb={4} pt={2}>
            <Stack spacing={2}>
              {navItems.map((item) => (
                <Box key={item.label}>
                  {item.children ? (
                    <>
                      <Flex
                        py={3}
                        px={4}
                        justify="space-between"
                        align="center"
                        cursor="pointer"
                        onClick={() => toggleMobileDropdown(item.label)}
                        bg="gray.50"
                        borderRadius="4px"
                        _hover={{
                          bg: "gray.100",
                        }}
                      >
                        <Text fontSize="15px" fontWeight="500" color="#333">
                          {item.label}
                        </Text>
                        <ChevronDownIcon
                          transform={openMobileDropdown === item.label ? "rotate(180deg)" : "rotate(0deg)"}
                          transition="transform 0.2s"
                        />
                      </Flex>
                      <Collapse in={openMobileDropdown === item.label}>
                        <VStack align="stretch" spacing={1} pl={4} pt={2}>
                          {item.children.map((child) => (
                            <Box
                              key={child.label}
                              py={2}
                              px={4}
                              cursor="pointer"
                              onClick={() => child.href && handleNavigation(child.href)}
                              _hover={{
                                color: "#00a651",
                              }}
                              fontSize="14px"
                              color="#666"
                            >
                              {child.label}
                            </Box>
                          ))}
                        </VStack>
                      </Collapse>
                    </>
                  ) : item.isButton ? (
                    <Button
                      w="full"
                      bg="#121c47"
                      color="white"
                      py={3}
                      fontSize="15px"
                      fontWeight="600"
                      borderRadius="4px"
                      onClick={() => item.href && handleNavigation(item.href)}
                      _hover={{
                        bg: "#0a1230",
                      }}
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <Box
                      py={3}
                      px={4}
                      cursor="pointer"
                      onClick={() => item.href && handleNavigation(item.href)}
                      bg="gray.50"
                      borderRadius="4px"
                      _hover={{
                        bg: "gray.100",
                        color: "#00a651",
                      }}
                      fontSize="15px"
                      fontWeight="500"
                      color="#333"
                    >
                      {item.label}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </Collapse>
      </Container>

      {/* SMS Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};